import React, { useState, useMemo } from "react";
import { Input, InputNumber, Modal, Form, Upload, Switch, message } from "antd";
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  ShopOutlined, UploadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useProductsCatalog } from "../../../hooks/useCatalogData";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { confirmDanger } from "../../../shared/ui/confirm";
import usePermissions from "../../../hooks/usePermissions";

const generateSku = () => `SKU-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const PosSubProducts = ({ onBack, onMutated }) => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission("products.add");
  const canEdit = hasPermission("products.edit");
  const canManageImages = hasPermission("products.images");
  const canDelete = hasPermission("products.delete");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [form] = Form.useForm();

  const { data: products, refresh } = useProductsCatalog();
  const currency = readCachedSystemConfig()?.currency_code || "IQD";

  const buildNameJson = (displayName, existing) => {
    try {
      const base = existing ? JSON.parse(existing) : {};
      return JSON.stringify({
        en: displayName, ar: displayName, ku: displayName,
        ...base,
        [i18n.language]: displayName,
      });
    } catch {
      return JSON.stringify({ en: displayName, ar: displayName, ku: displayName });
    }
  };

  const filtered = useMemo(() => {
    const t = search.toLowerCase();
    if (!t) return products;
    return products.filter((p) => {
      const name = parseLocalizedName(p.name, i18n.language, "").toLowerCase();
      return name.includes(t) || (p.sku || "").toLowerCase().includes(t);
    });
  }, [products, search, i18n.language]);

  const openAdd = () => {
    setEditItem(null);
    form.resetFields();
    setImageFile(null);
    setImagePreview(null);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setImageFile(null);
    setImagePreview(p.image_url ? getStorageUrl(p.image_url) : null);
    form.setFieldsValue({
      name: parseLocalizedName(p.name, i18n.language, ""),
      price: Number(p.price) || 0,
      category_id: p.category_id || undefined,
      unit_id: p.unit_id || undefined,
      sku: p.sku || "",
      is_stockable: !(p.is_stockable === false || p.is_stockable === 0 || p.is_stockable === "0" || p.is_stockable === "false"),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    form.resetFields();
    setEditItem(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      let uploadedImageUrl = null;
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("file", imageFile);
        const uploadRes = await apiClient.post("/products/image", imageFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedImageUrl = uploadRes.data.path;
      }

      const productData = {
        name: buildNameJson(values.name, editItem?.name),
        price: values.price ?? 0,
        is_stockable: values.is_stockable !== false ? 1 : 0,
      };
      productData.sku = values.sku || generateSku();
      if (values.category_id != null) productData.category_id = values.category_id;
      if (values.unit_id != null) productData.unit_id = values.unit_id;
      if (uploadedImageUrl) productData.image_url = uploadedImageUrl;

      if (editItem) {
        await apiClient.put(`/products/${editItem.id}`, productData);
        message.success(t("posProductUpdated", "Product updated"));
      } else {
        await apiClient.post("/products", productData);
        message.success(t("posProductAdded", "Product added"));
      }
      await refresh();
      onMutated();
      closeModal();
    } catch {
      message.error(t("posProductSaveFailed", "Failed to save product"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id, name) => {
    confirmDanger({
      title: t("posDeleteProductTitle", "Delete this product?"),
      content: (name ? `"${name}" — ` : "") + (t("posDeleteWarning", "This action cannot be undone.")),
      okText: t("delete", "Delete"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        setDeleting(id);
        try {
          await apiClient.delete(`/products/${id}`);
          await refresh();
          onMutated();
          message.success(t("posProductDeleted", "Product deleted"));
        } catch {
          message.error(t("posDeleteFailed", "Failed to delete"));
        } finally {
          setDeleting(null);
        }
      },
    });
  };

  return (
    <div className="pos-sub-view">
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className="pos-sub-icon"><ShopOutlined /></div>
          <h2 className="pos-sub-title">{t("posNavProducts", "Products")}</h2>
          <span className="pos-sub-tab-badge" style={{ fontSize: 12, padding: "2px 10px" }}>
            {filtered.length}
          </span>
        </div>
        <button type="button" className="pos-sub-back" onClick={onBack}>{t("posBackToPOS", "Back to POS")}</button>
      </div>

      <div className="pos-sub-toolbar">
        <Input
          prefix={<SearchOutlined />}
          placeholder={t("posProductsSearch", "Search products by name or SKU...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className="pos-sub-search"
        />
        {canAdd && (
          <button type="button" className="pos-sub-add-btn" onClick={openAdd}>
            <PlusOutlined /> {t("posProductsAdd", "Add Product")}
          </button>
        )}
      </div>

      <div className="pos-sub-list">
        {filtered.length === 0 ? (
          <div className="pos-sub-empty">
            <div className="pos-sub-empty-icon"><ShopOutlined /></div>
            <div className="pos-sub-empty-title">{t("posNoProductsFound", "No products found")}</div>
            <div className="pos-sub-empty-sub">{t("posProductsEmptyHint", "Click 'Add Product' to create your first product")}</div>
          </div>
        ) : (
          <div className="pos-mgmt-product-list">
            {filtered.map((p) => {
              const name = parseLocalizedName(p.name, i18n.language, `#${p.id}`);
              return (
                <div className="pos-mgmt-product-row" key={p.id}>
                  <div className="pos-mgmt-product-thumb">
                    {p.image_url
                      ? <img src={getStorageUrl(p.image_url)} alt="" />
                      : <ShopOutlined />}
                  </div>
                  <div className="pos-mgmt-product-body">
                    <div className="pos-mgmt-product-name">{name}</div>
                    <div className="pos-mgmt-product-meta">
                      {p.sku && <span className="pos-mgmt-badge pos-mgmt-badge-sku">#{p.sku}</span>}
                    </div>
                  </div>
                  <div className="pos-mgmt-product-price">
                    {formatCurrency(Number(p.price) || 0, currency)}
                  </div>
                  <div className="pos-sub-card-actions">
                    {canEdit && (
                      <button
                        type="button"
                        className="pos-sub-edit-btn"
                        onClick={() => openEdit(p)}
                        title={t("edit", "Edit")}
                      >
                        <EditOutlined />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="pos-sub-delete-btn"
                        onClick={() => handleDelete(p.id)}
                        disabled={deleting === p.id}
                        title={t("delete", "Delete")}
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={
          <span style={{ color: "#0891b2" }}>
            {editItem ? t("posProductsEdit", "Edit Product") : t("posProductsAdd", "Add Product")}
          </span>
        }
        onOk={() => form.submit()}
        okText={t("save", "Save")}
        okButtonProps={{ style: { background: "#0891b2", borderColor: "#0891b2", color: "#fff" } }}
        confirmLoading={saving}
        className="pos-sub-modal"
        width={500}
        zIndex={3000}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div style={{ display: "flex", gap: 14, marginBottom: 14, alignItems: "flex-start" }}>
            <div style={{
              width: 80, height: 80, borderRadius: 8, overflow: "hidden",
              border: "1.5px dashed #e5e7eb", background: "#f9fafb", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {imagePreview
                ? <img src={imagePreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <ShopOutlined style={{ fontSize: 28, color: "#d1d5db" }} />}
            </div>
            {canManageImages && <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>
                {t("posProductImage", "Product Image")}
              </div>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 4 * 1024 * 1024) {
                    message.error(t("productImageInvalid", "Use a JPG, PNG, or WebP image up to 4 MB."));
                    return Upload.LIST_IGNORE;
                  }
                  setImageFile(file);
                  setImagePreview(URL.createObjectURL(file));
                  return false;
                }}
              >
                <button type="button" style={{
                  height: 32, padding: "0 14px", borderRadius: 6,
                  border: "1px solid #e5e7eb", background: "#f9fafb",
                  color: "#374151", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                }}>
                  <UploadOutlined /> {t("posProductChooseImage", "Choose Image")}
                </button>
              </Upload>
              {imageFile && (
                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
                  {imageFile.name}
                </div>
              )}
            </div>}
          </div>

          <Form.Item
            name="name"
            label={t("posProductName", "Product Name")}
            rules={[{ required: true, message: t("nameRequired", "Name is required") }]}
          >
            <Input placeholder={t("posProductNamePlaceholder", "Enter product name")} className="pos-sub-modal-input" />
          </Form.Item>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item
              name="price"
              label={t("price", "Price")}
              style={{ flex: 1 }}
              rules={[{ required: true, message: t("posProductPriceRequired", "Price is required") }]}
            >
              <InputNumber min={0} placeholder="0" className="pos-sub-modal-number" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="sku" label={t("posProductSkuOptional", "SKU (optional)")} style={{ flex: 1 }}>
              <Input placeholder={t("posProductSkuPlaceholder", "Optional SKU code")} className="pos-sub-modal-input" />
            </Form.Item>
          </div>

          <Form.Item
            name="is_stockable"
            valuePropName="checked"
            initialValue={true}
            style={{ marginBottom: 0 }}
          >
            <Switch
              checkedChildren={t("posProductTrackStock", "Track Stock")}
              unCheckedChildren={t("posProductNoStockTracking", "No Stock Tracking")}
              style={{ marginTop: 4 }}
            />
          </Form.Item>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            {t("posProductStockTrackingHint", "Disable for services or products you don't track inventory for (e.g. labour, packaging)")}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PosSubProducts;
